import franceDepartments from './franceDepartments.json';
import { departmentCodeFromRaw } from './ignGeocode';

export interface FranceDepartment {
    code: number;
    /** Display code: '01', '33', '2A/2B'. */
    label: string;
    name: string;
}

interface DepartmentFeature {
    properties?: { code?: string; nom?: string };
}

const buildDepartments = (): FranceDepartment[] => {
    const byCode: Record<number, FranceDepartment> = {};
    ((franceDepartments as { features?: DepartmentFeature[] }).features || []).forEach((feature) => {
        const raw = String(feature.properties?.code || '');
        const code = departmentCodeFromRaw(raw);
        if (!code) {
            return;
        }
        // 2A and 2B share code 20 (see departmentCodeFromRaw): Corsica is selected as a whole.
        if (byCode[code]) {
            byCode[code] = { code, label: '2A/2B', name: 'Corse' };
            return;
        }
        byCode[code] = { code, label: raw.toUpperCase(), name: feature.properties?.nom || raw };
    });
    return Object.values(byCode).sort((a, b) => a.code - b.code);
};

export const DEPARTMENTS: FranceDepartment[] = buildDepartments();

const DEPARTMENTS_BY_CODE: Record<number, FranceDepartment> = DEPARTMENTS.reduce(
    (acc, department) => ({ ...acc, [department.code]: department }),
    {} as Record<number, FranceDepartment>,
);

export const departmentLabel = (code: number): string =>
    DEPARTMENTS_BY_CODE[code]?.label || String(code).padStart(2, '0');

export const departmentName = (code: number): string => DEPARTMENTS_BY_CODE[code]?.name || '';

export const formatDepartment = (code: number): string => {
    const name = departmentName(code);
    return name ? `${departmentLabel(code)} — ${name}` : departmentLabel(code);
};

const normalize = (value: string): string =>
    value
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim();

/** Match on the code prefix ('33', '2a') or anywhere in the accent-insensitive name ('herault'). */
export const searchDepartments = (query: string, limit = 8): FranceDepartment[] => {
    const q = normalize(query);
    if (!q) {
        return [];
    }
    return DEPARTMENTS.filter(
        (department) => normalize(department.label).startsWith(q) || normalize(department.name).includes(q),
    ).slice(0, limit);
};

/** Merge-in `code` keeping the list sorted, unlike toggleDepartmentCode it never removes. */
export const addDepartmentCode = (codes: number[], code: number): number[] =>
    codes.includes(code) ? codes : [...codes, code].sort((a, b) => a - b);

export interface FranceRegion {
    name: string;
    departmentCodes: number[];
}

/** Metropolitan regions (2016 map), to select a whole area in one click. Corsica = 20 (2A/2B). */
export const REGIONS: FranceRegion[] = [
    { name: 'Auvergne-Rhône-Alpes', departmentCodes: [1, 3, 7, 15, 26, 38, 42, 43, 63, 69, 73, 74] },
    { name: 'Bourgogne-Franche-Comté', departmentCodes: [21, 25, 39, 58, 70, 71, 89, 90] },
    { name: 'Bretagne', departmentCodes: [22, 29, 35, 56] },
    { name: 'Centre-Val de Loire', departmentCodes: [18, 28, 36, 37, 41, 45] },
    { name: 'Corse', departmentCodes: [20] },
    { name: 'Grand Est', departmentCodes: [8, 10, 51, 52, 54, 55, 57, 67, 68, 88] },
    { name: 'Hauts-de-France', departmentCodes: [2, 59, 60, 62, 80] },
    { name: 'Île-de-France', departmentCodes: [75, 77, 78, 91, 92, 93, 94, 95] },
    { name: 'Normandie', departmentCodes: [14, 27, 50, 61, 76] },
    { name: 'Nouvelle-Aquitaine', departmentCodes: [16, 17, 19, 23, 24, 33, 40, 47, 64, 79, 86, 87] },
    { name: 'Occitanie', departmentCodes: [9, 11, 12, 30, 31, 32, 34, 46, 48, 65, 66, 81, 82] },
    { name: 'Pays de la Loire', departmentCodes: [44, 49, 53, 72, 85] },
    { name: "Provence-Alpes-Côte d'Azur", departmentCodes: [4, 5, 6, 13, 83, 84] },
];

/** Selects the whole region, or removes it when it is already fully selected. */
export const toggleRegion = (codes: number[], region: FranceRegion): number[] => {
    const complete = region.departmentCodes.every((code) => codes.includes(code));
    if (complete) {
        return codes.filter((code) => !region.departmentCodes.includes(code));
    }
    return Array.from(new Set([...codes, ...region.departmentCodes])).sort((a, b) => a - b);
};

/** Same rule as the API: the postal code starts with the department; 20 = Corsica as a whole. */
export const postalCodeInDepartments = (postalCode: string | null | undefined, codes: number[]): boolean => {
    const value = (postalCode || '').trim();
    return value !== '' && codes.some((code) => value.startsWith(String(code).padStart(2, '0')));
};
